import { BathProductPage, BathProductConfig } from "@/components/bath/BathProductPage";
import { Sparkles, ShieldCheck, Wrench, Layers, TrendingUp, CheckCircle2, Home, Building2, Hotel } from "lucide-react";

/**
 * /:lang/bath-sanitary/aria-diamond
 * ARIA Diamond — modern diamond-texture freestanding solid surface bathtub.
 * Sourced and de-branded from KKR-B127 reference; rebuilt as Aureum VII piece.
 */
const config: BathProductConfig = {
  slug: "aria-diamond",
  category: "Freestanding Bathtub",
  productName: "ARIA Diamond Freestanding Solid Surface Bathtub",
  shortName: "ARIA Diamond",
  sku: "ARIA-DIAMOND-1824",
  schemaCategory: "Bathtubs > Freestanding > Solid Surface",
  metaTitle: "ARIA Diamond | Textured Solid Surface Bathtub",
  metaDescription:
    "ARIA Diamond — modern diamond-texture solid surface freestanding bathtub. Hand-carved hammered exterior, 1824mm, CE certified, MOQ-ready, EU shipping.",
  heroEyebrow: "Project Solutions · Sculptural Bathtub",
  heroHeadline: (
    <>
      Modern Diamond Texture{" "}
      <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">
        Solid Surface Bathtub
      </span>
    </>
  ),
  heroIntro:
    "Hand-carved diamond-quilted exterior. Seamless cast solid surface. A sculptural freestanding bathtub specified for primary suites, boutique hotels, and signature residential developments across Europe.",
  premiumSummary:
    "A 1824 mm freestanding solid surface bathtub built around a precision-machined diamond hammered texture. Architectural form, hospitality-grade durability, and trade pricing engineered for multi-unit developments.",
  galleryImages: [
    {
      src: "/images/bath/aria-diamond-hero.jpg",
      alt: "ARIA Diamond freestanding solid surface bathtub with diamond hammered texture — modern luxury bathroom hero",
      title: "ARIA Diamond Freestanding Bathtub — Hero",
      caption: "Hero · Diamond texture freestanding",
      description:
        "Hero of the ARIA Diamond 1824 mm freestanding solid surface bathtub in matte white, set against a panoramic landscape window with a black floor-mounted bath filler.",
    },
    {
      src: "/images/bath/aria-diamond-detail.jpg",
      alt: "ARIA Diamond bathtub close-up — precision diamond-quilted hammered solid surface texture",
      title: "ARIA Diamond — Diamond Texture Detail",
      caption: "Detail · Diamond hammered texture",
      description:
        "Macro detail of the precision-machined diamond-quilted hammered exterior of the ARIA Diamond solid surface bathtub in matte white.",
    },
    {
      src: "/images/bath/aria-diamond-top.jpg",
      alt: "ARIA Diamond freestanding bathtub matte white top-down studio view with chrome center drain",
      title: "ARIA Diamond — Matte White Top View",
      caption: "Studio · Matte white top view",
      description:
        "Studio top-down of the ARIA Diamond freestanding bathtub in matte white, showing the seamless oval inner basin and polished chrome centre pop-up drain.",
    },
    {
      src: "/images/bath/aria-diamond-noir.jpg",
      alt: "ARIA Diamond Noir matte black diamond-texture solid surface freestanding bathtub side view",
      title: "ARIA Diamond Noir — Matte Black Finish",
      caption: "Finish · Matte black noir",
      description:
        "ARIA Diamond in the matte black noir finish — a sculptural diamond-textured freestanding solid surface bathtub for darker, editorial bathroom palettes.",
    },
    {
      src: "/images/bath/aria-diamond-suite.jpg",
      alt: "ARIA Diamond bathtub installed in luxury hotel spa suite with travertine walls and brass tap",
      title: "ARIA Diamond — Luxury Hotel Suite Lifestyle",
      caption: "Lifestyle · Hotel spa suite",
      description:
        "Lifestyle scene: ARIA Diamond specified in a five-star hotel spa suite with travertine walls, micro-cement floor and brushed brass brassware.",
    },
  ],
  specs: [
    { label: "Dimensions", value: "1824 × 911 × 603 mm (L × W × H)" },
    { label: "Material", value: "Cast Solid Surface (Stone Resin Composite)" },
    { label: "Finish", value: "Matte White · Matte Black Noir (Gloss on request)" },
    { label: "Surface Texture", value: "Hand-carved Diamond Hammered Pattern" },
    { label: "Installation", value: "Freestanding · Floor-mounted" },
    { label: "Weight", value: "≈ 110 kg (empty)" },
    { label: "Capacity", value: "≈ 280 L to overflow" },
    { label: "Drainage", value: "Concealed centre pop-up · Chrome / Brushed Gold" },
    { label: "Certification", value: "CE · SGS · UL · cUPC" },
    { label: "Warranty", value: "10 years structural" },
    { label: "Packaging", value: "Foam + PE film + nylon strap + E0 plywood box" },
    { label: "MOQ", value: "From 5 units · Bulk pricing 20+" },
    { label: "Lead Time", value: "15–20 working days · 20ft container" },
  ],
  whyChoose: [
    {
      icon: Sparkles,
      title: "Signature diamond-quilted exterior",
      body: "A precision-machined diamond hammered texture that reads as bespoke sculpture, not catalogue plumbing — instantly differentiates marketing photography.",
    },
    {
      icon: TrendingUp,
      title: "Higher perceived value per unit",
      body: "A textured freestanding tub elevates listing photography, show-flat impact, and resale appeal — for a fraction of European-brand cost.",
    },
    {
      icon: ShieldCheck,
      title: "Solid surface durability",
      body: "Non-porous, repairable, warm to the touch. CE, SGS, UL and cUPC certified for residential and hospitality use, backed by a 10-year structural warranty.",
    },
    {
      icon: Wrench,
      title: "Specifier-friendly installation",
      body: "Standard EU plumbing rough-in, concealed centre pop-up drain. No structural reinforcement required for typical concrete slabs.",
    },
    {
      icon: Layers,
      title: "Reliable bulk supply",
      body: "MOQ from 5 units, bulk pricing from 20+. 15–20 working days production for a 20ft container — predictable for phased rollouts.",
    },
    {
      icon: CheckCircle2,
      title: "Margin-protective pricing",
      body: "Trade pricing structured to protect your project margin — luxury aesthetic without the European brand premium.",
    },
  ],
  perfectFor: [
    { icon: Home, title: "Branded Residences", body: "High-end residential and rental developments needing a signature focal-point bathtub." },
    { icon: Building2, title: "Private Villas", body: "Primary suites where the tub anchors the room and earns the listing photo." },
    { icon: Hotel, title: "Boutique Hotels", body: "Spa-grade guest bathrooms and suites needing repeatable, photographable luxury." },
    { icon: Wrench, title: "Premium Renovations", body: "Bathroom refurbishments targeting a distinct sculptural reposition." },
  ],
  longDescription: [
    "The ARIA Diamond is a modern freestanding solid surface bathtub built around a single signature: a hand-carved diamond hammered exterior that turns a functional bathtub into a sculptural object. Cast from a high-density stone-resin composite, it is monolithic in form, soft to the touch, and engineered for the way real European bathrooms get specified, installed, and used.",
    "Where acrylic feels hollow and cast iron is logistically punishing, this solid surface bath sits in a quiet middle: warm under the hand, resilient under daily use, and visually heavy enough to anchor a primary suite, a boutique hotel guest bathroom, or a high-end rental listing. The diamond-quilted exterior and seamless interior are precision-machined as a single form — no visible seams, no chrome edge banding, no plastic feel.",
    "For property developers building at scale, the value is straightforward. A textured freestanding tub photographs better than a built-in. Better photography drives faster lease-up, stronger off-plan sales, and higher perceived value per square metre. The ARIA Diamond was sourced specifically because it delivers that photograph — the one your marketing team needs — without the European luxury tax.",
    "For interior designers and architects, the diamond pattern is intentionally directional: it catches raking light, throws soft shadow during the day, and reads beautifully under warm sconce lighting at night. It pairs cleanly with travertine, micro-cement, walnut joinery, brushed brass, matte black brassware, and stone slab walls — the same vocabulary as the Italian and Scandinavian references your clients send through.",
    "Installation is deliberately specifier-friendly. Standard EU plumbing rough-in, concealed centre pop-up drain, no structural reinforcement required for typical concrete slabs. Each unit ships in a foam-padded E0 plywood box with PE film and nylon strap protection, and arrives in a single piece — no on-site assembly, no surprise components.",
    "Backed by CE, SGS, UL and cUPC certification and a 10-year structural warranty, the ARIA Diamond completes our luxury bathroom range alongside the ARIA Sculpt, ARIA Monolith, VENA basin and vanity programme, and the REM7 smart toilet flagship.",
  ],
};

const AriaDiamond = () => <BathProductPage config={config} />;
export default AriaDiamond;
